import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import { Icon } from "@/components/Icon";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { Tabs } from "@/components/Tabs";
import { useToast } from "@/context/ToastContext";
import { tenderApi } from "@/lib/api";
import { Tender, TenderLog } from "@/pages/Tenders/types";
import { date, price } from "@/utils/format";

interface TenderDetailModalProps {
    isOpen?: boolean;
    tender: Tender | null;
    onClose: () => void;
    onEdit?: (tender: Tender) => void;
    onChangeStatus?: (tender: Tender) => void;
    onDelete?: (tender: Tender) => void;
    onRefresh?: () => Promise<void>;
}

export const TenderDetailDrawer: React.FC<TenderDetailModalProps> = ({
    tender,
    onClose,
    onEdit,
    onChangeStatus,
    onDelete,
    onRefresh,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [activeTab, setActiveTab] = useState<
        "general" | "economic" | "categories" | "documentation" | "history"
    >("general");
    const [downloadingBc3, setDownloadingBc3] = useState<boolean>(false);

    const [logs, setLogs] = useState<TenderLog[] | null>(null);
    const [loadingLogs, setLoadingLogs] = useState<boolean>(false);

    React.useEffect(() => {
        if (tender?.id) {
            setLogs(null);
            setActiveTab("general");
        }
    }, [tender?.id]);

    React.useEffect(() => {
        if (
            activeTab === "history" &&
            logs === null &&
            tender &&
            !loadingLogs
        ) {
            void loadLogs();
        }
    }, [activeTab, tender, logs, loadingLogs]);

    const loadLogs = async () => {
        if (!tender) return;
        setLoadingLogs(true);
        try {
            const res = await tenderApi.logs(tender.id);
            setLogs(res.data);
        } catch (error) {
            console.error(error);
            showError(
                error instanceof Error ? error.message : t("common.error")
            );
        } finally {
            setLoadingLogs(false);
        }
    };

    if (!tender) {
        return null;
    }

    const handleDownloadBc3 = async (): Promise<void> => {
        if (!tender.bc3_file_path) {
            return;
        }

        setDownloadingBc3(true);
        try {
            const blob = await tenderApi.downloadBc3(tender.id);
            const fileName = tender.bc3_file_name || `${tender.code}.bc3`;
            saveAs(blob, fileName);
            showSuccess(t("developments.detail.task_downloaded"));
        } catch (error) {
            console.error(error);
            showError(
                error instanceof Error ? error.message : t("common.error")
            );
        } finally {
            setDownloadingBc3(false);
        }
    };

    const statusTag =
        typeof tender.status === "object" && tender.status !== null
            ? tender.status.tag || tender.status.slug
            : String(tender.status || "");

    const provinceName =
        typeof tender.province === "object" && tender.province !== null
            ? tender.province.name
            : tender.province || "";

    const countryName =
        typeof tender.country === "object" && tender.country !== null
            ? tender.country.name
            : tender.country || "";

    return (
        <Modal
            isOpen={tender !== null}
            onClose={onClose}
            title={t("tender.tender_detail")}
            maxWidth="4xl"
            footer={
                <div className="flex w-full justify-center gap-2">
                    {onDelete && (
                        <Button
                            variant="danger"
                            className="w-30"
                            icon="trash"
                            onClick={() => onDelete(tender)}
                            text={t("common.archive")}
                        />
                    )}
                    {onEdit && (
                        <Button
                            variant="warning"
                            className="w-30"
                            icon="pencil"
                            onClick={() => onEdit(tender)}
                            text={t("tender.action_edit")}
                        />
                    )}
                    <Button
                        className="w-30"
                        variant="secondary"
                        icon="x"
                        onClick={() => onClose()}
                        text={t("common.close")}
                    />
                </div>
            }
        >
            <div className="space-y-4">
                {/* Tabs */}
                <Tabs
                    activeTab={activeTab}
                    onChange={(t) => setActiveTab(t as any)}
                    items={[
                        { id: "general", label: t("tender.tabs.general") },
                        { id: "economic", label: t("tender.tabs.economic") },
                        {
                            id: "categories",
                            label: t("tender.tabs.categories"),
                        },
                        {
                            id: "documentation",
                            label: t("tender.tabs.documentation"),
                        },
                        { id: "history", label: t("tender.tabs.history") },
                    ]}
                    full
                    subtabs
                />

                {/* Tab content */}
                <div>
                    {activeTab === "general" && (
                        <div className="space-y-4">
                            <div className="space-y-3">
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_title")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.title || "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_code")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.code || "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_client")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.client?.name || "—"}
                                    </p>
                                    {tender.client?.tax_id && (
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {tender.client.tax_id}
                                        </p>
                                    )}
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_submission_date")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.submission_date
                                            ? date(
                                                  tender.submission_date,
                                                  false
                                              )
                                            : "—"}
                                        {tender.submission_time
                                            ? ` · ${tender.submission_time}`
                                            : ""}
                                    </p>
                                    {tender.submission_mode && (
                                        <p className="text-xs text-slate-400 mt-0.5">
                                            {tender.submission_mode}
                                        </p>
                                    )}
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500 mb-1.5">
                                        {t("tender.field_client_type")}
                                    </p>
                                    {(() => {
                                        const clientType =
                                            tender.client?.client_type ||
                                            tender.client_type;
                                        const clientTypeName = clientType?.name;
                                        if (!clientTypeName) {
                                            return (
                                                <span className="text-sm font-semibold text-slate-400">
                                                    —
                                                </span>
                                            );
                                        }
                                        const isPublic =
                                            clientTypeName
                                                .toLowerCase()
                                                .includes("públic") ||
                                            clientTypeName
                                                .toLowerCase()
                                                .includes("public");
                                        const isCivil = clientTypeName
                                            .toLowerCase()
                                            .includes("civil");
                                        const defaultFallback = isPublic
                                            ? "success"
                                            : isCivil
                                              ? "neutral"
                                              : "warning";
                                        const defaultIcon = isPublic
                                            ? "landmark"
                                            : isCivil
                                              ? "hardHat"
                                              : "house";

                                        const color =
                                            clientType?.color ||
                                            defaultFallback;
                                        const icon =
                                            clientType?.icon || defaultIcon;

                                        return (
                                            <Badge
                                                color={color}
                                                icon={icon}
                                                value={clientTypeName}
                                            />
                                        );
                                    })()}
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500 mb-1.5">
                                        {t("tender.field_work_type")}
                                    </p>
                                    {(() => {
                                        const workTypeName =
                                            tender.project_type?.name ||
                                            tender.work_type?.name;
                                        if (!workTypeName) {
                                            return (
                                                <span className="text-sm font-semibold text-slate-400">
                                                    —
                                                </span>
                                            );
                                        }
                                        const color =
                                            tender.project_type?.color ||
                                            tender.work_type?.color ||
                                            "success";
                                        const icon =
                                            tender.project_type?.icon ||
                                            tender.work_type?.icon;

                                        return (
                                            <Badge
                                                color={color}
                                                icon={icon || undefined}
                                                value={workTypeName}
                                            />
                                        );
                                    })()}
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3.5 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_address")}
                                    </p>
                                    <p className="mt-1 text-sm font-medium text-slate-800 dark:text-zinc-200">
                                        {[
                                            tender.address,
                                            tender.street_number,
                                            tender.postal_code,
                                            tender.city,
                                            provinceName,
                                            countryName,
                                        ]
                                            .filter(Boolean)
                                            .join(", ") || "—"}
                                    </p>
                                </div>
                            </div>

                            {tender.description && (
                                <div className="rounded-xl border border-slate-200 dark:border-white/10 p-4">
                                    <p className="text-xs font-semibold text-slate-500 mb-1">
                                        {t("tender.field_description")}
                                    </p>
                                    <p className="text-sm text-slate-700 dark:text-zinc-300 whitespace-pre-line">
                                        {tender.description}
                                    </p>
                                </div>
                            )}

                            {tender.valuation_criteria && (
                                <div className="rounded-xl border border-slate-200 dark:border-white/10 p-4">
                                    <p className="text-xs font-semibold text-slate-500 mb-1">
                                        {t("tender.field_valuation_criteria")}
                                    </p>
                                    <p className="text-sm text-slate-700 dark:text-zinc-300 whitespace-pre-line">
                                        {tender.valuation_criteria}
                                    </p>
                                </div>
                            )}

                            {tender.observations && (
                                <div className="rounded-xl border border-slate-200 dark:border-white/10 p-4">
                                    <p className="text-xs font-semibold text-slate-500 mb-1">
                                        {t("tender.field_observations")}
                                    </p>
                                    <p className="text-sm text-slate-700 dark:text-zinc-300 whitespace-pre-line">
                                        {tender.observations}
                                    </p>
                                </div>
                            )}

                            {tender.cancellation_reason && (
                                <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 dark:border-rose-900/30 dark:bg-rose-950/20">
                                    <p className="text-xs font-semibold text-rose-700 dark:text-rose-400 mb-1">
                                        {t("tender.field_cancellation_reason")}
                                    </p>
                                    <p className="text-sm text-rose-800 dark:text-rose-200 whitespace-pre-line">
                                        {tender.cancellation_reason}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "economic" && (
                        <div className="space-y-4">
                            <div className="space-y-3">
                                <div className="rounded-xl bg-blue-50/70 dark:bg-blue-950/20 p-4 border border-blue-200/80 dark:border-blue-900/40">
                                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                                        {t("tender.field_budget")}
                                    </p>
                                    <p className="mt-1 text-2xl font-bold text-blue-900 dark:text-blue-100">
                                        {tender.budget != null
                                            ? price(tender.budget)
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-4 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs font-semibold text-slate-500">
                                        {t("tender.field_deadline")}
                                    </p>
                                    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                                        {tender.deadline_period
                                            ? (() => {
                                                  const rawUnit =
                                                      typeof tender.deadline_unit ===
                                                      "string"
                                                          ? tender.deadline_unit
                                                          : (
                                                                tender.deadline_unit as any
                                                            )?.tag || "days";
                                                  const unit = String(
                                                      rawUnit || "days"
                                                  );
                                                  const baseUnit =
                                                      unit.endsWith("s")
                                                          ? unit.slice(0, -1)
                                                          : unit;
                                                  const unitKey =
                                                      tender.deadline_period ===
                                                      1
                                                          ? `tender.unit_${baseUnit}`
                                                          : `tender.unit_${baseUnit}s`;
                                                  return `${tender.deadline_period} ${t(unitKey)}`;
                                              })()
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_general_expenses")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.general_expenses_percentage !==
                                            null &&
                                        tender.general_expenses_percentage !==
                                            undefined
                                            ? `${tender.general_expenses_percentage}%`
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_industrial_profit")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.industrial_profit_percentage !==
                                            null &&
                                        tender.industrial_profit_percentage !==
                                            undefined
                                            ? `${tender.industrial_profit_percentage}%`
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_vat")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.vat_percentage !== null &&
                                        tender.vat_percentage !== undefined
                                            ? `${tender.vat_percentage}%`
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_payment_method")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.payment_method || "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t(
                                            "tender.field_provisional_guarantee"
                                        )}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.provisional_guarantee_percentage !==
                                            null &&
                                        tender.provisional_guarantee_percentage !==
                                            undefined
                                            ? `${tender.provisional_guarantee_percentage}%`
                                            : "—"}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 dark:bg-dark-box p-3 border border-slate-200/80 dark:border-white/5">
                                    <p className="text-xs text-slate-500">
                                        {t("tender.field_definitive_guarantee")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-zinc-200">
                                        {tender.definitive_guarantee_percentage !==
                                            null &&
                                        tender.definitive_guarantee_percentage !==
                                            undefined
                                            ? `${tender.definitive_guarantee_percentage}%`
                                            : "—"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "categories" && (
                        <div className="space-y-4">
                            {tender.classifications &&
                                tender.classifications.length > 0 && (
                                    <div className="space-y-2">
                                        {tender.classifications.map(
                                            (item, index) => {
                                                const groupTag = item.group?.tag
                                                    ? t(
                                                          `classification.${item.group.tag}`
                                                      )
                                                    : "";
                                                const groupLabel =
                                                    groupTag &&
                                                    groupTag !==
                                                        `classification.${item.group?.tag}`
                                                        ? groupTag
                                                        : item.group?.name ||
                                                          "";
                                                const subTag = item.subgroup
                                                    ?.tag
                                                    ? t(
                                                          `classification.${item.subgroup.tag}`
                                                      )
                                                    : "";
                                                const subLabel =
                                                    subTag &&
                                                    subTag !==
                                                        `classification.${item.subgroup?.tag}`
                                                        ? subTag
                                                        : item.subgroup?.name ||
                                                          "";
                                                const catTag = item.category
                                                    ?.tag
                                                    ? t(
                                                          `classification.${item.category.tag}`
                                                      )
                                                    : "";
                                                const catLabel =
                                                    catTag &&
                                                    catTag !==
                                                        `classification.${item.category?.tag}`
                                                        ? catTag
                                                        : item.category?.name ||
                                                          "";

                                                return (
                                                    <div
                                                        key={index}
                                                        className="p-3 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 flex flex-wrap items-center gap-2 text-xs"
                                                    >
                                                        {groupLabel && (
                                                            <Badge variant="primary">
                                                                {groupLabel}
                                                            </Badge>
                                                        )}
                                                        {subLabel && (
                                                            <Badge variant="secondary">
                                                                {subLabel}
                                                            </Badge>
                                                        )}
                                                        {catLabel && (
                                                            <Badge variant="warning">
                                                                {catLabel}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )}

                            {(!tender.classifications ||
                                tender.classifications.length === 0) &&
                                tender.categories &&
                                tender.categories.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {tender.categories.map(
                                            (cat: string) => (
                                                <Badge
                                                    key={cat}
                                                    variant="primary"
                                                >
                                                    {cat}
                                                </Badge>
                                            )
                                        )}
                                    </div>
                                )}

                            {(!tender.classifications ||
                                tender.classifications.length === 0) &&
                                (!tender.categories ||
                                    tender.categories.length === 0) && (
                                    <p className="text-sm text-slate-500">
                                        {t("common.no_results")}
                                    </p>
                                )}
                        </div>
                    )}

                    {activeTab === "documentation" && (
                        <div className="space-y-4">
                            <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 text-center bg-slate-50/50 dark:bg-dark-box">
                                <Icon
                                    value="paperclip"
                                    size={32}
                                    className="mx-auto text-primary mb-2"
                                />
                                <p className="font-semibold text-slate-800 dark:text-zinc-200 text-sm">
                                    {tender.bc3_file_name ||
                                        t("tender.field_bc3_file")}
                                </p>
                                {tender.bc3_percentage !== undefined && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        {t("tender.field_bc3_percentage")}:{" "}
                                        {tender.bc3_percentage}%
                                    </p>
                                )}

                                <div className="mt-4 flex justify-center gap-3">
                                    {tender.bc3_file_path && (
                                        <Button
                                            variant="primary"
                                            onClick={handleDownloadBc3}
                                            disabled={downloadingBc3}
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <Icon
                                                    value={
                                                        downloadingBc3
                                                            ? "refresh"
                                                            : "download"
                                                    }
                                                    size={14}
                                                    className={
                                                        downloadingBc3
                                                            ? "animate-spin"
                                                            : ""
                                                    }
                                                />
                                                <span>
                                                    {t(
                                                        "tender.action_download_bc3"
                                                    )}
                                                </span>
                                            </div>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "history" && (
                        <div className="space-y-3">
                            {loadingLogs ? (
                                <p className="text-xs text-slate-500 py-4 text-center">
                                    {t("common.loading")}
                                </p>
                            ) : logs && logs.length > 0 ? (
                                logs.map((log: TenderLog) => (
                                    <div
                                        key={log.id}
                                        className="rounded-xl border border-slate-200 dark:border-white/10 p-3.5 text-xs bg-slate-50 dark:bg-dark-box flex items-start gap-3"
                                    >
                                        <Icon
                                            value="checkCircle"
                                            size={16}
                                            className="text-primary shrink-0 mt-0.5"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-slate-800 dark:text-zinc-200">
                                                {log.action}
                                                {log.to_status
                                                    ? ` → ${t(`tender.status.${log.to_status}`)}`
                                                    : ""}
                                            </p>
                                            {log.notes && (
                                                <p className="text-slate-600 dark:text-zinc-400 mt-1">
                                                    {log.notes}
                                                </p>
                                            )}
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {log.user?.name
                                                    ? `${log.user.name} · `
                                                    : ""}
                                                {date(log.created_at, true)}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-xs text-slate-500 py-4 text-center">
                                    {t("tender.no_activity")}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export const TenderDetailModal = TenderDetailDrawer;
